/**
 * 바이트 업로드 진행률이 있는 XHR 래퍼 — MoFile·Basic·PublicUpload·FileDetailModal(맞춤본 올리기)이
 * 각자 들고 있던 XMLHttpRequest 복제를 하나로 모은다.
 *
 * ⚠️ Chromium 은 `upload.onload` 를 바이트가 다 나간 시점이 아니라 **응답 헤더가 올 때** 발화시킨다.
 *    그래서 서버가 응답 전에 뭔가 하면(썸네일 등) "올리는 중 100%" 에 멈춘 것처럼 보였다
 *    (2026-10-11 몬스터펍 보고). 바이트 완료는 progress(loaded>=total)로 잡아 onBytesDone 으로 알린다.
 */
export interface UploadWithProgressOpts {
  url: string;
  method?: 'POST' | 'PUT';
  formData: FormData;
  headers?: Record<string, string>;
  withCredentials?: boolean;      // 기본 true(세션). 비로그인 업로드는 false + 토큰 헤더.
  onProgress?: (percent: number) => void;
  /** 바이트가 모두 나간 시점(서버 처리 대기 시작). */
  onBytesDone?: () => void;
}

export interface UploadResult {
  status: number;
  text: string;
  json: any | null;
}

export interface UploadHandle {
  promise: Promise<UploadResult>;
  abort: () => void;
}

export function uploadWithProgress(opts: UploadWithProgressOpts): UploadHandle {
  const xhr = new XMLHttpRequest();
  let bytesDoneFired = false;
  const fireBytesDone = () => {
    if (bytesDoneFired) return;
    bytesDoneFired = true;
    opts.onBytesDone?.();
  };
  const promise = new Promise<UploadResult>((resolve, reject) => {
    xhr.upload.onprogress = (ev) => {
      if (!ev.lengthComputable) return;
      opts.onProgress?.((ev.loaded / ev.total) * 100);
      if (ev.loaded >= ev.total) fireBytesDone();
    };
    xhr.upload.onload = () => fireBytesDone();  // 다른 브라우저용 보강
    xhr.onload = () => {
      fireBytesDone();
      let json: any = null;
      try { json = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { json = null; }
      resolve({ status: xhr.status, text: xhr.responseText, json });
    };
    xhr.onerror = () => reject(new Error('network'));
    xhr.onabort = () => reject(new Error('aborted'));
    xhr.open(opts.method ?? 'POST', opts.url);
    xhr.withCredentials = opts.withCredentials ?? true;
    for (const [k, v] of Object.entries(opts.headers ?? {})) xhr.setRequestHeader(k, v);
    xhr.send(opts.formData);
  });
  return { promise, abort: () => xhr.abort() };
}
